import { Navigate, Routes, Route } from 'react-router-dom';
import { domMax, LazyMotion, MotionConfig } from 'motion/react';
import Login from './pages/Login';
import GoogleComplete from './pages/GoogleComplete';
import AdminUsers from './pages/AdminUsers';
import Backlog from './pages/Backlog';
import WorkQueue from './pages/WorkQueue';
import NewOpsItem from './pages/NewOpsItem';
import NewServiceRequest from './pages/NewServiceRequest';
import ServiceRequests from './pages/ServiceRequests';
import ServiceRequestPage from './pages/ServiceRequest';
import ServiceRequestSettings from './pages/ServiceRequestSettings';
import InitiativeTemplateSettings from './pages/InitiativeTemplateSettings';
import ServiceRequestTimeline from './pages/ServiceRequestTimeline';
import ServiceRequestImplementations from './pages/ServiceRequestImplementations';
import ServiceRequestImplementation from './pages/ServiceRequestImplementation';
import Board from './pages/Board';
import Initiatives from './pages/Initiatives';
import InitiativePage from './pages/Initiative';
import NewInitiative from './pages/NewInitiative';
import WorkItemPage from './pages/WorkItem';
import ParentSubtaskBoard from './pages/ParentSubtaskBoard';
import ViewsList from './pages/ViewsList';
import ViewPage from './pages/ViewPage';
import AssignedToMe from './pages/AssignedToMe';
import Resources from './pages/Resources';
import ResourceDetail from './pages/ResourceDetail';
import Projects from './pages/Projects';
import Archives, { ArchivesRedirect } from './pages/Archives';
import BrowseRedirect from './pages/BrowseRedirect';
import ProjectSettings from './pages/ProjectSettings';
import OrgMembers from './pages/OrgMembers';
import Webhooks from './pages/Webhooks';
import WhatsApp from './pages/WhatsApp';
import Profile from './pages/Profile';
import Tokens from './pages/Tokens';
import Releases from './pages/Releases';
import ReleaseDetail from './pages/ReleaseDetail';
import Wiki from './pages/Wiki';
import WikiSpace from './pages/WikiSpace';
import ProtectedRoute from './components/ProtectedRoute';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import { OverlayProvider } from './components/work/overlay-context';
import OverlayRoot from './components/work/OverlayRoot';
import { TooltipProvider } from './components/ui/tooltip';
import { Toaster } from './components/ui/sonner';
import { SessionProvider } from './components/session-context';

export default function App() {
  return (
    // `strict` fails fast if a `motion.*` component is used instead of `m.*`,
    // which would pull Motion's full bundle in eagerly. Loading `domMax` via a
    // lazy chunk was measured and made the bundle larger, because `m` and
    // `AnimatePresence` are imported statically and share its feature modules.
    // `domMax` is needed for layout animations; `domAnimation` is smaller.
    <LazyMotion features={domMax} strict>
    <MotionConfig reducedMotion="user">
    <SessionProvider>
    <TooltipProvider>
    <OverlayProvider>
      <Toaster />
      <OverlayRoot />
      <Routes>
        <Route path="/" element={<ProtectedRoute><Navigate to="/my-work" replace /></ProtectedRoute>} />
        <Route path="/my-work" element={<ProtectedRoute><AssignedToMe /></ProtectedRoute>} />
        <Route path="/my-work/completed" element={<Navigate to="/my-work" replace />} />
        <Route path="/resources" element={<ProtectedRoute><Resources /></ProtectedRoute>} />
        <Route path="/resources/:userId" element={<ProtectedRoute><ResourceDetail /></ProtectedRoute>} />
        <Route path="/resources/:userId/completed" element={<Navigate to=".." relative="path" replace />} />
        <Route path="/login" element={<Login />} />
        <Route path="/login/google" element={<GoogleComplete />} />
        <Route path="/incidents/new" element={<ProtectedRoute><NewOpsItem type="incident" /></ProtectedRoute>} />
        <Route path="/incidents" element={<ProtectedRoute><WorkQueue type="incident" /></ProtectedRoute>} />
        <Route path="/problems/new" element={<ProtectedRoute><NewOpsItem type="problem" /></ProtectedRoute>} />
        <Route path="/problems" element={<ProtectedRoute><WorkQueue type="problem" /></ProtectedRoute>} />
        <Route path="/service-requests/new" element={<ProtectedRoute><NewServiceRequest /></ProtectedRoute>} />
        <Route path="/service-requests" element={<ProtectedRoute><ServiceRequests /></ProtectedRoute>} />
        <Route path="/service-requests/calendar" element={<ProtectedRoute><ServiceRequestTimeline /></ProtectedRoute>} />
        <Route path="/service-requests/implementations" element={<ProtectedRoute><ServiceRequestImplementations /></ProtectedRoute>} />
        <Route path="/service-requests/implementations/:id" element={<ProtectedRoute><ServiceRequestImplementation /></ProtectedRoute>} />
        <Route path="/projects" element={<ProtectedRoute><Projects /></ProtectedRoute>} />
        <Route path="/projects/:projectId/backlog" element={<ProtectedRoute><Backlog /></ProtectedRoute>} />
        <Route path="/projects/:projectId/tasks" element={<ProtectedRoute><Backlog /></ProtectedRoute>} />
        <Route path="/projects/:projectId/incidents" element={<ProtectedRoute><Navigate to="/incidents" replace /></ProtectedRoute>} />
        <Route path="/projects/:projectId/problems" element={<ProtectedRoute><Navigate to="/problems" replace /></ProtectedRoute>} />
        <Route path="/projects/:projectId/board" element={<ProtectedRoute><Board /></ProtectedRoute>} />
        <Route path="/projects/:projectId/views" element={<ProtectedRoute><ViewsList /></ProtectedRoute>} />
        <Route path="/projects/:projectId/views/:viewId" element={<ProtectedRoute><ViewPage /></ProtectedRoute>} />
        <Route path="/views/:viewId" element={<ProtectedRoute><ViewPage /></ProtectedRoute>} />
        <Route path="/projects/:projectId/work-items/:workItemId/board" element={<ProtectedRoute><ParentSubtaskBoard /></ProtectedRoute>} />
        <Route path="/projects/:projectId/work-items/:workItemId" element={<ProtectedRoute><WorkItemPage /></ProtectedRoute>} />
        <Route path="/projects/:projectId/service-requests/:workItemId" element={<ProtectedRoute><ServiceRequestPage /></ProtectedRoute>} />
        <Route path="/browse/:workItemKey" element={<ProtectedRoute><BrowseRedirect /></ProtectedRoute>} />
        <Route path="/projects/:projectId/completed" element={<ProtectedRoute><Archives /></ProtectedRoute>} />
        <Route path="/projects/:projectId/archives" element={<ProtectedRoute><ArchivesRedirect /></ProtectedRoute>} />
        <Route path="/initiatives" element={<ProtectedRoute><Initiatives /></ProtectedRoute>} />
        <Route path="/initiatives/backlog" element={<Navigate to="/initiatives?layout=backlog" replace />} />
        <Route path="/initiatives/new" element={<ProtectedRoute><NewInitiative /></ProtectedRoute>} />
        <Route path="/initiatives/:workItemId" element={<ProtectedRoute><InitiativePage /></ProtectedRoute>} />
        <Route path="/releases" element={<ProtectedRoute><Releases /></ProtectedRoute>} />
        <Route path="/releases/:releaseId" element={<ProtectedRoute><ReleaseDetail /></ProtectedRoute>} />
        <Route path="/wiki" element={<ProtectedRoute><Wiki /></ProtectedRoute>} />
        <Route path="/wiki/org/*" element={<ProtectedRoute><WikiSpace kind="org" /></ProtectedRoute>} />
        <Route path="/wiki/projects/:projectId/*" element={<ProtectedRoute><WikiSpace kind="project" /></ProtectedRoute>} />
        <Route path="/projects/:projectId/settings/*" element={<ProtectedRoute><ProjectSettings /></ProtectedRoute>} />
        <Route path="/settings/members" element={<ProtectedRoute><OrgMembers /></ProtectedRoute>} />
        <Route path="/settings/webhooks" element={<ProtectedRoute orgAdmin><Webhooks /></ProtectedRoute>} />
        <Route path="/settings/webhooks/:id" element={<ProtectedRoute orgAdmin><Webhooks /></ProtectedRoute>} />
        <Route path="/settings/whatsapp" element={<ProtectedRoute orgAdmin><WhatsApp /></ProtectedRoute>} />
        <Route path="/settings/service-requests" element={<ProtectedRoute orgAdmin><ServiceRequestSettings /></ProtectedRoute>} />
        <Route path="/settings/business-requirements" element={<ProtectedRoute roles={['admin']}><InitiativeTemplateSettings /></ProtectedRoute>} />
        <Route path="/settings/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/settings/tokens" element={<ProtectedRoute><Tokens /></ProtectedRoute>} />
        <Route path="/forgot" element={<ForgotPassword />} />
        <Route path="/reset" element={<ResetPassword />} />
        <Route path="/admin/users" element={<ProtectedRoute roles={['admin']}><AdminUsers /></ProtectedRoute>} />
      </Routes>
    </OverlayProvider>
    </TooltipProvider>
    </SessionProvider>
    </MotionConfig>
    </LazyMotion>
  );
}
